/**
 * PR15 + PR18 — `canShareItem`
 *
 * The single predicate that gates ItemDetail's Share Record block,
 * Shared-with live list, and the entry point to the Send-to-a-friend
 * modal. Lifted out of ItemDetailScreen.tsx so it's:
 *   - directly unit-testable (no need to mount the whole screen)
 *   - reusable by any future surface that exposes a "Share this item"
 *     affordance (e.g. an Items-grid quick action)
 *
 * **PR18 update:** "ownership" alone wasn't enough. PR15 covered
 * non-owned items (borrowers, share-claim duplicates) but did NOT
 * cover the case where the *owner* views their own item AFTER it's
 * been lent out — to a crew member or via a /share/{token} link.
 * The Share Record block was rendering on those screens, inviting
 * a logical bug: lending the same item twice.
 *
 * Five signals decide whether the viewer can create a share:
 *
 *   1. `viewerIsOwner` — the row's `user_id` matches the logged-in
 *      user. Backend's `POST /users/inventory/{id}/share` is gated
 *      on ownership and 404s (leak-safe) otherwise.
 *
 *   2. `claimedFromShareId == null` — a borrower's *duplicated*
 *      inventory row carries a non-null claimed_from_share_id (set
 *      by BE#160/BE#170 on /share/{token}/claim). Even though the
 *      duplicate's user_id is the borrower (so viewerIsOwner === true
 *      for that row in the DB sense), it's NOT the original — the
 *      borrower must not re-share something they only borrowed.
 *
 *   3. `isShareable !== false` — the owner-controlled opt-out flag
 *      on the original row. Defaults to true (treat null/undefined
 *      as "yes, shareable"). When backend exposes a future toggle
 *      UI we honor it here automatically.
 *
 *   4. Crew handoff possession: `currentCustodianUserId` is either
 *      null OR equals the viewer. If it points to someone else, the
 *      item is physically with a crew member and the owner can't
 *      hand it to a stranger via share at the same time.
 *
 *   5. Share-link possession: `outboundShareStatus` is NOT 'CLAIMED'
 *      or 'RETURNED'. Those statuses mean an active share-link is
 *      in flight — the item is conceptually lent. (COMPLETED,
 *      DECLINED, CANCELLED, PENDING all permit a new share.)
 *
 * Returns true ONLY when all five pass.
 *
 * For nuanced UX, callers should additionally check
 * `whyCantShare(input)` to choose copy explaining why the affordance
 * is hidden (e.g. "Currently lent — recall to share again" vs
 * "You don't own this item").
 */
export interface CanShareItemInput {
  viewerIsOwner: boolean;
  claimedFromShareId: number | null | undefined;
  isShareable: boolean | null | undefined;
  /** PR18 — current custodian on the row. When non-null AND different
   *  from the viewer, the item has been handed off (crew handoff). */
  currentCustodianUserId?: number | null;
  /** PR18 — for the viewer-as-owner case, the resolved viewer user id
   *  used to compare against currentCustodianUserId. Optional; when
   *  omitted, the crew-handoff branch is skipped. */
  viewerUserId?: number | null;
  /** PR18 — status of the matched outbound share for this item, if any.
   *  CLAIMED or RETURNED mean a share-link is currently active. Other
   *  statuses (or null) leave the item available for new shares. */
  outboundShareStatus?:
    | 'PENDING'
    | 'CLAIMED'
    | 'RETURNED'
    | 'DECLINED'
    | 'CANCELLED'
    | 'COMPLETED'
    | null
    | undefined;
}

/** Reason `canShareItem` returned false. UI can use this to show
 *  the *right* explanation instead of silently disappearing. */
export type CantShareReason =
  | 'not_owner'
  | 'share_claim_duplicate'
  | 'opted_out'
  | 'lent_in_crew'
  | 'lent_via_share';

export function canShareItem(input: CanShareItemInput): boolean {
  return whyCantShare(input) == null;
}

/** Returns the first reason the share affordance is gated. Returns
 *  null when sharing is allowed. Caller picks the message. */
export function whyCantShare(input: CanShareItemInput): CantShareReason | null {
  if (!input.viewerIsOwner) return 'not_owner';
  if (input.claimedFromShareId != null) return 'share_claim_duplicate';
  if (input.isShareable === false) return 'opted_out';
  // PR18 — currently-out checks. Order: in-crew first (more common),
  // then share-link.
  if (
    input.currentCustodianUserId != null &&
    input.viewerUserId != null &&
    input.currentCustodianUserId !== input.viewerUserId
  ) {
    return 'lent_in_crew';
  }
  if (
    input.outboundShareStatus === 'CLAIMED' ||
    input.outboundShareStatus === 'RETURNED'
  ) {
    return 'lent_via_share';
  }
  return null;
}
